// Flattens GET-shaped state (relation fields as nested {id,...} objects)
// back into the raw-id shape every POST/PUT expects, and always resends
// every field - mirrors conversations-creator-canvas/conversationPayload.js.
// Necessary here because ExerciseTemplateElementService/
// ExerciseTemplateInteractiveElementService PUT does a raw repository.save()
// of the request body with no prior load - any omitted field comes through
// as null and overwrites that column.

function idOf(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === "object") return value.id;
    return value;
}

export function buildExerciseTemplateUpdatePayload(template, changes = {}) {
    const merged = { ...template, ...changes };
    return {
        id: merged.id,
        name: merged.name,
        description: merged.description,
        level: idOf(merged.level),
        classification: idOf(merged.classification),
        // type became a relation to a new ExerciseType entity (was a free
        // string) - 2026-08-15 backend contract change.
        type: idOf(merged.type),
        // dAdd is only ever set server-side on create(), never touched by
        // update() - but update() still does a raw save() of this whole
        // object, so omitting it here would null out the original creation
        // date. dMod is server-overwritten on every update() regardless of
        // what's sent, but included anyway for the same full-resend reason.
        dAdd: merged.dAdd,
        dMod: merged.dMod,
    };
}

// exerciseId is always taken as an explicit argument, never read off `block`
// - the exercise FK is required on the backend and a stale/missing value
// 500s (DataIntegrityViolationException), so the route param is the only
// value this is ever allowed to come from.
export function buildElementPayload(exerciseId, block, changes = {}) {
    const merged = { ...block, ...changes };
    return {
        id: merged.id,
        exercise: String(exerciseId),
        container: merged.container ? String(idOf(merged.container)) : null,
        type: merged.type,
        text: merged.text || null,
        meta: merged.meta || null,
        config: merged.config || null,
        position: merged.position,
        blurEnabled: !!merged.blurEnabled,
        blurRequiresApproval: merged.blurRequiresApproval !== false,
    };
}

export function buildInteractiveElementPayload(exerciseId, block, changes = {}) {
    const merged = { ...block, ...changes };
    return {
        id: merged.id,
        exercise: String(exerciseId),
        container: merged.container ? String(idOf(merged.container)) : null,
        type: merged.type,
        text: merged.text || null,
        meta: merged.meta || null,
        config: merged.config || null,
        conversation: merged.conversation ? String(idOf(merged.conversation)) : "",
        grid: merged.grid ? String(idOf(merged.grid)) : "",
        position: merged.position,
        blurEnabled: !!merged.blurEnabled,
        blurRequiresApproval: merged.blurRequiresApproval !== false,
    };
}

// No name/label field - purely a visual-configuration object, same
// reasoning as courses/coursesPayload.js's buildTopicContainerPayload.
export function buildContainerPayload(exerciseId, container, changes = {}) {
    const merged = { ...container, ...changes };
    return {
        id: merged.id,
        exercise: String(exerciseId),
        layout: merged.layout,
        config: merged.config || null,
        position: merged.position,
    };
}
